/**
 * Persisted customer notes.
 *
 * Replaces the in-memory `notes` / `addNote` / `deleteNote` on CrmProvider,
 * which wrote to React state only — a note appeared to save and was gone on
 * reload. Everything here goes through /v1/customers/{id}/notes, with
 * optimistic add/delete and rollback on failure so the UI still feels instant.
 */

import { useCallback, useEffect, useState } from 'react';
import { customersService } from './services';
import { useToast } from './toast';

const asList = (v) => (Array.isArray(v) ? v : (v?.data ?? v?.content ?? []));

export function useCustomerNotes(customerId) {
  const { toast } = useToast();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(!!customerId);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!customerId) { setNotes([]); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const rows = await customersService.notes(customerId);
      setNotes(asList(rows));
    } catch (e) {
      setError(e);
      // Keep whatever we had; an empty list here would look like "no notes"
      // which is exactly the failure mode this hook exists to remove.
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const add = useCallback(async (body, extra = {}) => {
    const text = (body || '').trim();
    if (!text || !customerId) return null;
    const tempId = `tmp-${Date.now()}`;
    const optimistic = {
      id: tempId, customerId, body: text, pinned: false,
      authorName: 'You', createdAt: new Date().toISOString(), _pending: true, ...extra,
    };
    setNotes((prev) => [optimistic, ...prev]);
    try {
      const saved = await customersService.addNote(customerId, { body: text, ...extra });
      const row = saved?.data ?? saved;
      setNotes((prev) => prev.map((n) => (n.id === tempId ? { ...optimistic, ...row, _pending: false } : n)));
      return row;
    } catch (e) {
      setNotes((prev) => prev.filter((n) => n.id !== tempId));
      toast(`Note not saved: ${e?.response?.data?.message || e?.message || 'unknown error'}`, { variant: 'danger' });
      throw e;
    }
  }, [customerId, toast]);

  const remove = useCallback(async (noteId) => {
    let removed = null;
    setNotes((prev) => { removed = prev.find((n) => n.id === noteId) || null; return prev.filter((n) => n.id !== noteId); });
    try {
      await customersService.deleteNote(noteId);
    } catch (e) {
      if (removed) setNotes((prev) => [removed, ...prev]);
      toast(`Could not delete: ${e?.response?.data?.message || e?.message || 'unknown error'}`, { variant: 'danger' });
      throw e;
    }
  }, [toast]);

  const togglePin = useCallback(async (noteId) => {
    const current = notes.find((n) => n.id === noteId);
    if (!current) return;
    const next = !current.pinned;
    setNotes((prev) => prev.map((n) => (n.id === noteId ? { ...n, pinned: next } : n)));
    try {
      await customersService.updateNote(noteId, { pinned: next });
    } catch (e) {
      setNotes((prev) => prev.map((n) => (n.id === noteId ? { ...n, pinned: !next } : n)));
      toast('Could not update the note', { variant: 'danger' });
    }
  }, [notes, toast]);

  return { notes, loading, error, reload: load, add, remove, togglePin };
}
